import { apiClient } from './client'
import { API_ENDPOINTS } from '@/utils/constants'
import type {
  User,
  TeacherPageResponse,
  CreateUserData,
  UpdateUserData,
  UserFilters,
  PaginatedResponse,
  AuthResponse,
} from '@/types'

interface ApiResponse<T> {
  success?: boolean
  message?: string
  data?: T
}

const BASE_URL = API_ENDPOINTS.USERS

interface RawPaginatedResponse {
  data?: User[]
  meta?: {
    current_page?: number
    last_page?: number
    per_page?: number
    total?: number
    /** Суммарное занятое аудио-хранилище всех учителей (только админ) */
    storage_total_bytes?: number
  }
  current_page?: number
  last_page?: number
  per_page?: number
  total?: number
}

/** Список пользователей; для админа дополнительно суммарное занятое хранилище */
export type UsersListResponse = PaginatedResponse<User> & { storage_total_bytes?: number }

function normalizePaginatedResponse(raw: RawPaginatedResponse): UsersListResponse {
  const data = Array.isArray(raw?.data) ? raw.data : []
  const meta = raw?.meta || {}
  return {
    data,
    current_page: meta.current_page ?? raw.current_page ?? 1,
    last_page: meta.last_page ?? raw.last_page ?? 1,
    per_page: meta.per_page ?? raw.per_page ?? 20,
    total: meta.total ?? raw.total ?? 0,
    storage_total_bytes: meta.storage_total_bytes,
  }
}

export const usersApi = {
  /**
   * Получить список пользователей
   * Для админа — все с фильтрами; для учителя — ученики; для ученика — пустой список
   */
  getAll: async (filters?: UserFilters): Promise<UsersListResponse> => {
    const params = new URLSearchParams()
    if (filters?.page) params.append('page', String(filters.page))
    if (filters?.per_page) params.append('per_page', String(filters.per_page))
    if (filters?.search) params.append('search', filters.search)
    if (filters?.role) params.append('role', filters.role)
    if (filters?.status) params.append('status', filters.status)
    if (filters?.subscription) params.append('subscription', filters.subscription)
    if (filters?.sort_by) params.append('sort_by', filters.sort_by)
    if (filters?.sort_order) params.append('sort_order', filters.sort_order)
    if (filters?.contract_status) params.append('contract_status', filters.contract_status)
    if (filters?.group_id != null) params.append('group_id', String(filters.group_id))

    try {
      const response = await apiClient.get<User[] | RawPaginatedResponse>(`${BASE_URL}?${params.toString()}`)
      if (Array.isArray(response.data)) {
        return {
          data: response.data,
          current_page: 1,
          last_page: 1,
          per_page: 20,
          total: response.data.length,
        }
      }
      return normalizePaginatedResponse(response.data as RawPaginatedResponse)
    } catch (error: unknown) {
      console.error('Error fetching users:', error)
      const err = error as { response?: { data?: { message?: string } }; message?: string }
      throw new Error(err.response?.data?.message || err.message || 'Ошибка загрузки пользователей')
    }
  },

  /**
   * Получить своего учителя и историю договоров (ученик). GET /users/teacher
   */
  getTeacher: async (): Promise<TeacherPageResponse> => {
    const response = await apiClient.get<{ data?: TeacherPageResponse }>(API_ENDPOINTS.USERS_TEACHER)
    const payload = response.data?.data
    if (!payload) {
      throw new Error('Ошибка загрузки данных учителя')
    }
    return {
      teacher: payload.teacher ?? null,
      contract: payload.contract ?? null,
      history: Array.isArray(payload.history) ? payload.history : [],
    }
  },

  /**
   * Получить пользователя по ID
   */
  getById: async (id: number): Promise<User> => {
    try {
      interface UserResponse {
        data?: User
        id?: number
        message?: string
      }
      const response = await apiClient.get<UserResponse | User>(`${BASE_URL}/${id}`)

      const data = response.data as UserResponse | User
      if (data && 'data' in data && data.data) {
        return data.data
      }

      if (data && 'id' in data && data.id) {
        return data as User
      }

      throw new Error((data as UserResponse)?.message || 'Ошибка загрузки пользователя')
    } catch (error: unknown) {
      console.error('Error fetching user:', error)
      const err = error as { response?: { status?: number; data?: { message?: string } }; message?: string }
      const msg = err.response?.data?.message || err.message || 'Ошибка загрузки пользователя'
      const errorObj = new Error(msg) as Error & { status?: number }
      errorObj.status = err.response?.status
      throw errorObj
    }
  },

  /**
   * Создать пользователя (ученика) с автоматическим созданием договора
   */
  create: async (data: CreateUserData): Promise<User> => {
    const response = await apiClient.post<ApiResponse<User>>(BASE_URL, data)

    if (response.data?.data) {
      return response.data.data
    }

    throw new Error(response.data?.message || 'Ошибка создания пользователя')
  },

  /**
   * Обновить пользователя (с обновлением/созданием договора).
   * Аватар загружается отдельным multipart-запросом.
   */
  update: async (id: number, data: UpdateUserData, avatarFile?: File): Promise<User> => {
    const hasDataToUpdate = Object.values(data).some((v) => v !== undefined && v !== null)

    let lastResponse: { data?: ApiResponse<User> } | undefined

    if (hasDataToUpdate) {
      lastResponse = await apiClient.put<ApiResponse<User>>(`${BASE_URL}/${id}`, data)
    }

    if (avatarFile) {
      const form = new FormData()
      form.append('avatar', avatarFile)
      lastResponse = await apiClient.post<ApiResponse<User>>(`${BASE_URL}/${id}/avatar`, form)
    }

    if (lastResponse?.data?.data) {
      return lastResponse.data.data
    }

    throw new Error(lastResponse?.data?.message || 'Ошибка обновления пользователя')
  },

  /**
   * Удалить пользователя (только для админа)
   */
  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`${BASE_URL}/${id}`)
  },

  /**
   * Отправить ссылку на сброс пароля на email пользователя (учитель — своим ученикам, админ — любому).
   */
  sendPasswordReset: async (userId: number): Promise<void> => {
    await apiClient.post(`${BASE_URL}/${userId}/send-password-reset`)
  },

  /**
   * Расторгнуть договор (для учителя)
   */
  cancelContract: async (userId: number): Promise<User> => {
    const response = await apiClient.post<ApiResponse<User>>(
      `${BASE_URL}/${userId}/cancel-contract`
    )

    if (response.data?.data) {
      return response.data.data
    }

    throw new Error(response.data?.message || 'Ошибка расторжения договора')
  },

  /**
   * Войти под пользователем (impersonation). Только для админа. POST /admin/users/:id/impersonate
   */
  impersonate: async (userId: number): Promise<AuthResponse> => {
    interface ImpersonateResponse {
      user?: User
      token?: string
      token_type?: string
      expires_in?: number
      data?: {
        user?: User
        token?: string
      }
    }
    const response = await apiClient.post<ImpersonateResponse | AuthResponse>(
      `${API_ENDPOINTS.ADMIN.USERS}/${userId}/impersonate`
    )
    const body = response.data as ImpersonateResponse | AuthResponse
    if ('user' in body && 'token' in body) {
      return body as AuthResponse
    }
    const impersonateBody = body as ImpersonateResponse
    return {
      user: impersonateBody.user ?? impersonateBody.data?.user ?? {} as User,
      token: impersonateBody.token ?? impersonateBody.data?.token ?? '',
      token_type: impersonateBody.token_type ?? 'bearer',
      expires_in: impersonateBody.expires_in,
    }
  },
}
